// @flow
import './AgentScript.scss';

import { HomeIcon } from '@boi/core/lib';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon, IconProp } from '@fortawesome/react-fontawesome';
import type { Node } from 'react';
import React, { Children, useContext, useEffect, useState } from 'react';

import { greyDark, midGrayColor } from '../../constants/colors';
import { withPrefix } from '../../helpers/HtmlHelper';
import UserContext from '../../UserContext';

type AgentScriptProps = {
  children: Node
};

export const AgentScript = ({ children }: AgentScriptProps) => {
  const withClass = withPrefix('c-AgentScript');
  const numPages = Children.count(children);
  const hasMultiplePages = numPages > 1;

  const [clicked, setClicked] = useState<boolean>(false);
  const [tooltipShown, setTooltipShown] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const { agentScriptPending, setAgentScriptPending } = useContext(UserContext);

  useEffect(() => setAgentScriptPending(true), []);

  useEffect(() => {
    if (clicked && currentPage === numPages - 1) {
      setAgentScriptPending(false);
    }
  }, [currentPage]);

  const onIconClicked = () => {
    setTooltipShown((v: boolean) => !v);
    setClicked(true);
    !hasMultiplePages && setAgentScriptPending(false);
  };

  const paginationButton = (
    icon: IconProp,
    isEnabled: boolean,
    pageAction: number => number
  ) => (
    <div
      className={withClass('__script-pagination-button')}
      onClick={() => isEnabled && setCurrentPage(pageAction)}
    >
      <FontAwesomeIcon
        icon={icon}
        color={isEnabled ? greyDark : midGrayColor}
      />
    </div>
  );

  const paginationPanel = () => {
    const canGoBack = currentPage > 0;
    const canGoForward = currentPage + 1 < numPages;

    return (
      <div className={withClass('__script-pagination')}>
        {paginationButton(faArrowLeft, canGoBack, (p: number) => p - 1)}
        <p className={withClass('__script-pagination-info')}>
          {`Page ${currentPage + 1} (of ${numPages})`}
        </p>
        {paginationButton(faArrowRight, canGoForward, (p: number) => p + 1)}
      </div>
    );
  };

  const displayScripts = () => (
    <div className={withClass('__script-panel')}>
      <div
        className={withClass('__script-text')}
        key={`scriptText_${currentPage}`}
      >
        {/* $FlowFixMe */}
        {hasMultiplePages ? children[currentPage] : children}
      </div>
      {hasMultiplePages && paginationPanel()}
    </div>
  );

  const showSmallIcon = () => {
    return clicked || !agentScriptPending;
  }

  return (
    <>
      {tooltipShown && displayScripts()}
      <div
        className={withClass(
          '__floating-button',
          (showSmallIcon()) && '__clicked',
          !agentScriptPending && '__all-pages-read'
        )}
        onClick={onIconClicked}
      >
        <HomeIcon color="white" solid={false} />
      </div>
    </>
  );
};
